/**
 * Textes de la page /v10 (OpenBroHome), FR et EN.
 * `**mot**` = mot qui porte le pitch : rendu en blanc et plus lourd, le reste en gris.
 * Aucun chiffre ici qui ne vienne de l'offre réelle (config/offer.js ou l'espace membre).
 */

import V30 from './openBroV20Copy';

const COPY = {
  fr: {
    lang: 'FR',
    switchLabel: 'Switch to English',
    nav: [['#chemins', 'Pourquoi'], ['#appel', 'L’appel'], ['#bros', 'Les bros'], ['#campus', 'L’espace membre'], ['#prix', 'Prix'], ['#faq', 'FAQ']],
    login: 'Connexion',
    headerCta: 'Rejoindre',
    eyebrow: 'Mentorat privé avec Gaut Bonac',
    title: 'Un mentor.\nUn plan.\nChaque mois.',
    lead: 'Business, argent, sport, alimentation, contenu, relations. Chaque mois, **on s’appelle**, on règle **ce qui te bloque**, et tu repars avec **ton plan des 30 prochains jours**.',
    between: 'Entre deux appels : **6 formations** et **une communauté privée** pour passer à l’exécution.',
    cta: (price) => `Rejoindre pour ${price}/mois`,
    ctaShort: 'Rejoindre',
    secondary: 'Voir les deux chemins',
    reassure: ['Sans engagement', 'Résiliable à tout moment', 'Accès immédiat'],
    period: '/mois',

    cardKicker: 'Ton premier appel',
    cardTitle: 'Choisis ton créneau.',
    cardBody: 'Je t’appelle au numéro que tu indiques au paiement. Le créneau est bloqué dès que c’est réglé.',
    slotsLabel: 'Créneaux disponibles',
    bookCta: (label) => `Réserver ${label}`,
    bookFallback: 'Réserver mon appel',
    cardFoot: (price) => `Inclus dans l’abonnement à ${price}/mois`,
    toasts: [
      (slot) => ['Bienvenue dans OpenBro', `Ton créneau : ${slot}`, 'Encore libre'],
      () => ['Bienvenue dans OpenBro', '−50 %. 99 €, normalement.', '49 €/mois'],
      () => ['Bienvenue dans OpenBro', 'Sans engagement', 'Résiliable à tout moment'],
    ],
    toastClose: 'Fermer',

    // Urgence : uniquement des faits du code (créneaux calculés, réservables jusqu'à 1 h 30 avant, 49 €).
    takeSlot: (label) => `Je prends ${label}, maintenant`,
    headerSlot: (time) => `Réserver ${time}`,
    barSlot: 'Ton créneau',
    barTake: 'Je le prends',
    barUntil: (left) => `Réservable encore ${left}`,
    heroUrge: ({ label, left }) => `**Ton appel : ${label}.** Réservable encore **${left}**. Après, il disparaît.`,
    leftFmt: { day: 'j', hour: 'h', min: 'min', sec: 's' },
    urge: {
      hero: ({ left }) => `**Il se réserve tout de suite.** Encore ${left}, ensuite le créneau passe.`,
      truth: ({ label }) => `**Ce soir peut être le dernier comme ça.** Ton appel est déjà calé : **${label}.**`,
      stories: ({ label }) => `Eux, ils ont commencé par un appel. **Le tien, c’est ${label}.**`,
      paths: ({ left }) => `**Si tu attends, le créneau passe.** Tu restes au même point. Encore ${left}.`,
      time: ({ label }) => `**Ton prochain créneau : ${label}.** Tu le prends, ou tu le laisses passer.`,
      call: ({ left }) => `**Il se prend maintenant.** Réservable encore ${left}, ensuite il disparaît.`,
      wins: ({ label }) => `**Ils ont tous commencé par un premier appel.** Le tien est prêt : ${label}.`,
      mentor: ({ label }) => `**Mais pour ça, il faut un premier appel.** Le tien : ${label}.`,
      price: ({ left }) => `**49 €, aujourd’hui.** Ton créneau se réserve encore ${left}.`,
      final: ({ label, left }) => `**${label}.** Réservable encore ${left}. **Après, c’est un mois de plus au même point.**`,
    },

    truthPill: 'Ta vie, là, maintenant',
    truthTitle: 'Il est 2 h du mat’. **Tu scrolles encore.**',
    truth: [
      '**Burger froid, energy drink, chambre en vrac.** Tu te dis que demain tu t’y mets. Tu te l’es déjà dit hier.',
      'Ce n’est pas un manque de volonté. **Tout autour de toi est fait pour te garder là** : le téléphone, la bouffe facile, les vidéos des autres qui avancent pendant que toi, tu regardes.',
      '**OpenBro n’est pas une formation de plus.** C’est un rendez-vous par mois avec quelqu’un qui te sort de là : **un plan pour ton assiette, ton entraînement et ton business.** Et le mois suivant, **on vérifie que tu l’as fait.**',
    ],

    storiesTitle: 'Ils ont changé. **Toi aussi.**',
    stories: { adam: ['La salle', '100 kg au squat'], ines: ['L’assiette', 'Meal prep le dimanche'], theo: ['Le business', 'Site en ligne en 30 jours'], lucas: ['La vie', 'Debout à 6 h 30'] },

    pathsPill: 'Deux chemins',
    pathsTitle: 'Tout seul **vs** OpenBro',
    aloneTitle: 'Tout seul',
    alone: [
      'Des heures de vidéos, rien ne bouge',
      'Des conseils qui se contredisent',
      'Tu bloques, personne ne répond',
      'Tu manges ce qui traîne',
      'Tu t’entraînes sans plan',
      'Tu recommences lundi. Encore.',
    ],
    aloneCta: 'Continuer seul',
    aloneHref: 'https://fr.wikipedia.org/wiki/Nomenclature_d%27activit%C3%A9s_fran%C3%A7aise',
    withTitle: 'OpenBro',
    with: [
      'Un appel privé avec Gaut chaque mois',
      'Ton plan 30 jours : alimentaire, entraînement, business',
      '6 formations, 100+ leçons',
      'Une communauté privée qui avance',
      'Le suivi de tes habitudes',
      'Sans engagement, résiliable à tout moment',
    ],

    timePill: 'Chaque mois compte',
    timeTitle: V30.time.title, // « Le temps t’est compté »
    time: [
      'Tu peux passer le mois qui vient **à te dire que tu commences lundi.** Ou le passer avec **un plan écrit, et quelqu’un qui te le redemande.**',
      '**Qu’as-tu fait des 30 derniers jours ?** Sois honnête.',
    ],
    planPill: 'Ton plan',
    planTitle: 'Trois plans. **Une seule direction.**',
    plan: [
      '**Plan alimentaire** : quoi manger, et quand, pour tenir ton objectif.',
      '**Plan d’entraînement** : tes séances de la semaine, dans l’ordre.',
      '**Plan business** : les actions qui font avancer ton projet ou ta carrière.',
      'On le construit **ensemble, pendant l’appel.**',
    ],
    planShot: 'Capture réelle : formation Nutrition & Performance',

    callPill: 'Un appel par mois',
    callTitle: 'Un appel.\n**Chaque mois.**',
    callTitleSlot: (label) => `Ton appel.\n**${label}.**`,
    call: (minutes) => [
      `Tu choisis ton créneau **avant de payer**. Je t’appelle au numéro que tu donnes${minutes ? ` : **${minutes} min en tête-à-tête.**` : '.'}`,
      'On fait le point sur ta situation, et tu repars **avec tes priorités du mois.** Pas un cours. **Une conversation.**',
    ],
    callShot: 'Capture réelle : réservation sur mobile',

    formationsPill: 'Les formations',
    formationsTitle: 'Six domaines. **Une seule direction : avancer.**',
    formationsBody: 'Des formations concrètes, **données par des gens qui pratiquent.** Tu commences par **ce qui compte pour toi.**',
    formations: {
      musculation: ['Musculation & Discipline', 'De zéro à intermédiaire : technique, programmation, progression.'],
      nutrition: ['Nutrition & Performance', 'Alimentation, suppléments et un système simple à tenir.'],
      reseaux: ['E-commerce & Réseaux sociaux', 'Lancer ta marque, vendre en ligne et faire grandir un compte qui inspire confiance.'],
      startup: ['Startup', 'Ton idée, ici, trouve son associé. Un bro pour lancer le projet avec toi.'],
      montage: ['Montage vidéo', 'Le rythme qui retient, les accroches qui arrêtent le scroll, un rendu pro.'],
      ia: ['IA & Automatisation', 'Faire bosser plusieurs modèles pour toi, et vendre des services IA.'],
    },

    filmPill: 'Regarde',
    filmPlay: 'Lancer la vidéo',
    filmTitle: 'Deux issues. **Une décision, aujourd’hui.**',
    stayKicker: 'Ne rien changer',
    stayTitle: 'Rester où tu es',
    stay: 'Même routine, même assiette, mêmes excuses. **Dans un an, tu te poses les mêmes questions.**',
    goKicker: 'Avancer',
    go: '**Un appel par mois, un plan, 6 formations et une communauté.** Résiliable quand tu veux.',

    winsPill: 'Dans le groupe',
    winsTitle: 'Les bros **avancent.**',
    winsBody: 'À quoi ressemble le groupe privé : **des bros qui se tirent vers le haut**, tous les jours.',
    wins: {
      yanis: 'Séance jambes et cardio avant le taf. Je suis trempé 🦵 qui vient demain 7 h ?',
      sarah: 'Chirashi du dimanche. Zéro kebab cette semaine le Goat 😅',
      adam: 'Premier plan tourné. Le bro m’a dit de couper le début. Ça tient.',
      sami: 'Deux ans que je la matais. Un bro m’a dit laquelle prendre.',
      enzo: 'Téléphone dans la cuisine, couché avant minuit. Trois semaines que je tiens. Ça paraît bête mais je suis plus le même',
      maxime: 'J’ai même trouvé quelqu’un qui m’a aidé à faire l’import, alors que je ne m’y connaissais pas.',
    },
    groupName: 'OpenBro · Les bros',

    campusPill: 'Ce que tu ouvres',
    campusTitle: 'Six campus. **Ouverts dès le paiement.**',
    campus: {
      musculation: 'Musculation & Discipline',
      reseaux: 'E-commerce & Réseaux sociaux',
      montage: 'Montage vidéo',
      ia: 'IA & Automatisation',
      nutrition: 'Nutrition & Performance',
      filming: 'Tournage & Filming',
    },
    lessons: (n) => `${n} leçons`,
    vaultLock: 'Réservé aux membres',
    vaultText: '',

    mentorPill: 'Pourquoi un appel',
    mentorTitle: 'Quelqu’un qui te dit **la vérité.**',
    mentor: [
      'Tes potes te disent que ça va. Ta famille aussi. **Moi, je te dis où tu en es vraiment.**',
      'Ce qui marche. Ce qui te plombe. **Ce que tu fais dès demain.**',
      'Trente jours plus tard, je t’appelle. **Tu l’as fait, ou pas.** On le voit tout de suite.',
    ],
    mentorSign: 'Gaut Bonac',

    choiceTitle: 'Maintenant, **ou encore un mois comme celui-là.**',
    nothingTitle: 'Ne rien faire',
    nothingSub: 'Continuer comme avant',
    nothingCost: 'Encore un mois perdu',
    nothing: [
      'Scroller en attendant la motivation',
      'Manger ce qui traîne',
      'T’entraîner sans plan',
      'Remettre ton projet à lundi',
      'Rester seul avec tes questions',
    ],
    nothingCta: 'Rester comme je suis',
    nothingHref: 'https://www.netflix.com',
    actTitle: 'Passer à l’action',
    actSub: 'Commencer aujourd’hui',
    perDay: 'Moins de 2 € par jour.',
    perks: [
      'Un appel privé avec moi chaque mois',
      'Ton plan 30 jours : alimentaire, entraînement, business',
      'Les 6 formations et leurs 100+ leçons',
      'La communauté privée',
      'Le suivi de tes habitudes et le Monk Mode',
      'Sur mobile et ordinateur',
    ],
    priceFine: 'Sans engagement. Tu résilies quand tu veux.',
    secure: 'Paiement sécurisé par Stripe',
    crypto: 'Payer en USDC (Solana)',

    faqPill: 'FAQ',
    faqTitle: 'Questions **fréquentes**',
    faq: [
      ['Et si j’attends un peu ?', 'Tu peux. Mais **ton créneau ne se réserve que jusqu’à 1 h 30 avant l’heure.** Ensuite il disparaît. **Et rien ne bouge tant que tu ne réserves pas** : même routine, même assiette, même projet en attente.'],
      ['Pourquoi aujourd’hui ?', `${V30.lock[0]} **49 €, sans engagement, et l’accès est immédiat.**`],
      ['C’est pour qui ?', 'Pour toi si tu veux avancer sur **ton business, ton argent, ton corps ou ta vie perso**, et que tu es prêt à passer à l’action. **Si tu cherches une formule magique, tu l’as trouvée.**'],
      ['Comment se passe l’appel ?', 'Tu choisis ton créneau **avant de payer**. Je t’appelle au numéro indiqué au paiement. On fait le point sur ta situation et **tu repars avec tes priorités du mois.**'],
      ['Que se passe-t-il juste après le paiement ?', 'Tu actives ton compte avec ton prénom et ton email. Tu te connectes avec un code reçu par email, et **tu as accès à tout** : formations, suivi, communauté.'],
      ['Je suis débutant, c’est un problème ?', 'Non. **Les formations partent de zéro**, et l’appel part de **ta situation**, pas d’un programme standard.'],
      ['Je peux arrêter quand je veux ?', 'Oui. **L’abonnement est sans engagement**, tu le résilies quand tu veux. Les mois déjà payés ne sont pas remboursés.'],
      ['Tu garantis des résultats ?', 'Oui. **Je t’apporte un plan, un regard extérieur et un cadre.** Les résultats, tu les as.'],
      ['Les vidéos et les messages, c’est qui ?', 'Ce sont **des mises en situation**, pas des membres filmés ni de vraies conversations. Les campus et les écrans de l’espace membre, eux, **viennent du vrai produit.**'],
    ],
    faqMore: 'Une autre question ? Écris à',

    finalTitle: V30.lock[2], // « Bloque ton prix dès maintenant avant qu’il n’augmente. »
    finalPill: 'C’est ta dernière chance !',
    finalBro: '**Et pas tout seul.** Dans OpenBro, tu trouves ton bro. **Celui avec qui tu montes ta boîte.** Celui qui t’attend à la salle à 7 h. **On progresse à deux**, pas seul devant des vidéos.',

    footerTag: 'by Gaut Bonac',
    terms: 'Conditions générales',
    privacy: 'Confidentialité',
    rights: 'Tous droits réservés.',
    membersWord: 'membres',
    followersLabel: 'abonnés sur TikTok et Instagram',
  },
  en: {
    lang: 'EN',
    switchLabel: 'Passer en français',
    nav: [['#chemins', 'Why'], ['#appel', 'The call'], ['#bros', 'The bros'], ['#campus', 'Member area'], ['#prix', 'Price'], ['#faq', 'FAQ']],
    login: 'Log in',
    headerCta: 'Join',
    eyebrow: 'Private mentorship with Gaut Bonac',
    title: 'One mentor.\nOne plan.\nEvery month.',
    lead: 'Business, money, fitness, food, content, relationships. Every month **we get on a call**, clear **what’s blocking you**, and you leave with **your plan for the next 30 days**.',
    between: 'Between calls: **6 programs** and **a private community** to help you follow through.',
    cta: (price) => `Join for ${price}/month`,
    ctaShort: 'Join',
    secondary: 'See the two paths',
    reassure: ['No commitment', 'Cancel anytime', 'Instant access'],
    period: '/month',

    cardKicker: 'Your first call',
    cardTitle: 'Pick your time.',
    cardBody: 'I call you on the number you give at checkout. The slot is yours as soon as you pay.',
    slotsLabel: 'Open slots',
    bookCta: (label) => `Book ${label}`,
    bookFallback: 'Book my call',
    cardFoot: (price) => `Included in the ${price}/month subscription`,
    toasts: [
      (slot) => ['Welcome to OpenBro', `Your slot: ${slot}`, 'Still open'],
      () => ['Welcome to OpenBro', '−50%. Normally €99.', '€49/month'],
      () => ['Welcome to OpenBro', 'No commitment', 'Cancel anytime'],
    ],
    toastClose: 'Close',

    takeSlot: (label) => `Take ${label}, right now`,
    headerSlot: (time) => `Book ${time}`,
    barSlot: 'Your slot',
    barTake: 'Take it',
    barUntil: (left) => `Bookable for ${left} more`,
    heroUrge: ({ label, left }) => `**Your call: ${label}.** Bookable for **${left}** more. Then it’s gone.`,
    leftFmt: { day: 'd', hour: 'h', min: 'min', sec: 's' },
    urge: {
      hero: ({ left }) => `**You lock it in right now.** ${left} left, then the slot passes.`,
      truth: ({ label }) => `**Tonight can be the last night like this.** Your call is already set: **${label}.**`,
      stories: ({ label }) => `They started with one call. **Yours is ${label}.**`,
      paths: ({ left }) => `**If you wait, the slot passes.** You stay where you are. ${left} left.`,
      time: ({ label }) => `**Your next slot: ${label}.** Take it, or let it pass.`,
      call: ({ left }) => `**You take it now.** Bookable for ${left} more, then it’s gone.`,
      wins: ({ label }) => `**Every one of them started with a first call.** Yours is ready: ${label}.`,
      mentor: ({ label }) => `**But it starts with a first call.** Yours: ${label}.`,
      price: ({ left }) => `**€49, today.** Your slot is bookable for ${left} more.`,
      final: ({ label, left }) => `**${label}.** Bookable for ${left} more. **After that, it’s another month in the same place.**`,
    },

    truthPill: 'Your life, right now',
    truthTitle: 'It’s 2 a.m. **You’re still scrolling.**',
    truth: [
      '**Cold burger, energy drink, messy room.** You tell yourself you’ll start tomorrow. You said that yesterday.',
      'It’s not a lack of willpower. **Everything around you is built to keep you there**: the phone, the easy food, videos of other people moving forward while you watch.',
      '**OpenBro isn’t just another course.** It’s a monthly meeting with someone who gets you out of there: **a plan for your plate, your training and your business.** And next month, **we check that you did it.**',
    ],

    storiesTitle: 'They changed. **So can you.**',
    stories: { adam: ['The gym', '100 kg squat'], ines: ['The plate', 'Meal prep every Sunday'], theo: ['Business', 'Site live in 30 days'], lucas: ['Daily life', 'Up at 6:30'] },

    pathsPill: 'Two paths',
    pathsTitle: 'On your own **vs** OpenBro',
    aloneTitle: 'On your own',
    alone: [
      'Hours of videos, nothing moves',
      'Advice that contradicts itself',
      'You get stuck, nobody answers',
      'You eat whatever is around',
      'You train without a plan',
      'You start again on Monday. Again.',
    ],
    aloneCta: 'Keep going alone',
    aloneHref: 'https://en.wikipedia.org/wiki/Standard_Industrial_Classification',
    withTitle: 'OpenBro',
    with: [
      'A private call with Gaut every month',
      'Your 30-day plan: food, training, business',
      '6 programs, 100+ lessons',
      'A private community that moves',
      'Habit tracking',
      'No commitment, cancel anytime',
    ],

    timePill: 'Every month counts',
    timeTitle: 'Your time is **running out**', // traduction de V30.time.title
    time: [
      'You can spend next month **telling yourself you’ll start on Monday.** Or spend it with **a written plan, and someone who asks you about it.**',
      '**What did you do with the last 30 days?** Be honest.',
    ],
    planPill: 'Your plan',
    planTitle: 'Three plans. **One direction.**',
    plan: [
      '**Food plan**: what to eat, and when, to hold your goal.',
      '**Training plan**: your sessions for the week, in order.',
      '**Business plan**: the actions that move your project or career forward.',
      'We build it **together, during the call.**',
    ],
    planShot: 'Real screenshot: Nutrition & Performance program',

    callPill: 'One call a month',
    callTitle: 'One call.\n**Every month.**',
    callTitleSlot: (label) => `Your call.\n**${label}.**`,
    call: (minutes) => [
      `You pick your slot **before you pay**. I call you on the number you give${minutes ? `: **${minutes} min, one to one.**` : '.'}`,
      'We look at where you are, and you leave **with your priorities for the month.** Not a class. **A conversation.**',
    ],
    callShot: 'Real screenshot: booking on mobile',

    formationsPill: 'The programs',
    formationsTitle: 'Six fields. **One direction: forward.**',
    formationsBody: 'Practical programs, **taught by people who actually do it.** Start with **whatever matters most to you.**',
    formations: {
      musculation: ['Strength & Discipline', 'Beginner to intermediate: technique, programming, progression.'],
      nutrition: ['Nutrition & Performance', 'Food, supplements and a simple system you can keep.'],
      reseaux: ['E-commerce & Social media', 'Launch your brand, sell online and grow an account people trust.'],
      startup: ['Startup', 'Your idea finds its co-founder here. A bro to launch the project with you.'],
      montage: ['Video editing', 'Pacing that holds attention, hooks that stop the scroll, a pro finish.'],
      ia: ['AI & Automation', 'Put several models to work for you, and sell AI services.'],
    },

    filmPill: 'Watch',
    filmPlay: 'Play the video',
    filmTitle: 'Two outcomes. **One decision, today.**',
    stayKicker: 'Change nothing',
    stayTitle: 'Stay where you are',
    stay: 'Same routine, same plate, same excuses. **A year from now, you ask yourself the same questions.**',
    goKicker: 'Move forward',
    go: '**One call a month, a plan, 6 programs and a community.** Cancel whenever you want.',

    winsPill: 'In the group',
    winsTitle: 'The bros **are moving.**',
    winsBody: 'What the private group looks like: **bros pulling each other up**, every day.',
    wins: {
      yanis: 'Legs and cardio before work. I’m soaked 🦵 who’s in tomorrow 7am?',
      sarah: 'Sunday chirashi. Zero kebab this week, the Goat 😅',
      adam: 'First video shot. A bro told me to cut the opening. It holds.',
      sami: 'Two years I’d been staring at it. A bro told me which one to get.',
      enzo: 'Phone stays in the kitchen, in bed before midnight. Three weeks now. Sounds dumb but I’m not the same guy',
      maxime: 'I even found someone who helped me handle the import, even though I knew nothing about it.',
    },
    groupName: 'OpenBro · The bros',

    campusPill: 'What you open',
    campusTitle: 'Six campuses. **Open the moment you pay.**',
    campus: {
      musculation: 'Strength & Discipline',
      reseaux: 'E-commerce & Social media',
      montage: 'Video editing',
      ia: 'AI & Automation',
      nutrition: 'Nutrition & Performance',
      filming: 'Filming',
    },
    lessons: (n) => `${n} lessons`,
    vaultLock: 'Members only',
    vaultText: '',

    mentorPill: 'Why a call',
    mentorTitle: 'Someone who tells you **the truth.**',
    mentor: [
      'Your friends say you’re fine. So does your family. **I tell you where you really stand.**',
      'What works. What drags you down. **What you do starting tomorrow.**',
      'Thirty days later, I call you. **You did it, or you didn’t.** We see it right away.',
    ],
    mentorSign: 'Gaut Bonac',

    choiceTitle: 'Now, **or another month like this one.**',
    nothingTitle: 'Do nothing',
    nothingSub: 'Keep going as before',
    nothingCost: 'Another month gone',
    nothing: [
      'Scroll while waiting for motivation',
      'Eat whatever is around',
      'Train without a plan',
      'Push your project to Monday',
      'Stay alone with your questions',
    ],
    nothingCta: 'Stay as I am',
    nothingHref: 'https://www.netflix.com',
    actTitle: 'Take action',
    actSub: 'Start today',
    perDay: 'Less than €2 a day.',
    perks: [
      'One private call with me every month',
      'Your 30-day plan: food, training, business',
      'All 6 programs and their 100+ lessons',
      'The private community',
      'Habit tracking and Monk Mode',
      'On phone and computer',
    ],
    priceFine: 'No commitment. Cancel whenever you want.',
    secure: 'Secure payment by Stripe',
    crypto: 'Pay in USDC (Solana)',

    faqPill: 'FAQ',
    faqTitle: 'Common **questions**',
    faq: [
      ['What if I wait a bit?', 'You can. But **your slot can only be booked until 1h30 before its time.** Then it’s gone. **And nothing moves until you book**: same routine, same plate, same project on hold.'],
      ['Why today?', '**Now is the perfect time to lock in your price and grab this offer before it’s too late.** **€49, no commitment, instant access.**'],
      ['Who is it for?', 'For you if you want to move forward on **your business, money, body or personal life**, and you’re ready to act. **If you’re looking for a magic formula, you just found it.**'],
      ['How does the call work?', 'You pick your slot **before paying**. I call you on the number you give at checkout. We look at your situation and **you leave with your priorities for the month.**'],
      ['What happens right after I pay?', 'You activate your account with your first name and email. You log in with a code sent by email, and **you get access to everything**: programs, tracking, community.'],
      ['I’m a beginner. Is that a problem?', 'No. **The programs start from zero**, and the call starts from **your situation**, not a standard plan.'],
      ['Can I stop whenever I want?', 'Yes. **No commitment**, cancel whenever you want. Months already paid are not refunded.'],
      ['Do you guarantee results?', 'Yes. **You get a plan, an outside view and structure.** The results come with it.'],
      ['Who are the people in the videos and messages?', 'They are **dramatizations**, not filmed members or real conversations. The campuses and member-area screens, however, **come from the real product.**'],
    ],
    faqMore: 'Another question? Email',

    finalTitle: '**Lock in your price now, before it goes up.**', // traduction de V30.lock[2]
    finalPill: 'This is your last chance!',
    finalBro: '**And not alone.** In OpenBro, you find your bro. **The one you build a company with.** The one waiting for you at the gym at 7am. **We level up in pairs**, not alone in front of videos.',

    footerTag: 'by Gaut Bonac',
    terms: 'Terms',
    privacy: 'Privacy',
    rights: 'All rights reserved.',
    membersWord: 'members',
    followersLabel: 'followers on TikTok and Instagram',
  },
};

export default COPY;
